import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Page visibility settings consumed from GET /api/pages.
 * The admin dashboard (separate Next.js app) writes to the shared
 * `page_settings` MongoDB collection. This context fetches those
 * settings on full page load, caches briefly for client-side nav,
 * and falls back to "show everything" if the call fails.
 */

const PageSettingsContext = createContext(null);

// Default fallback used when the API is unreachable. Keeps the nav
// working even if /api/pages returns an error. Must mirror the
// current hardcoded nav structure so nothing visibly breaks.
const FALLBACK_PAGES = [
  { page_name: "Home",    route: "/",        is_visible: true, display_order: 1 },
  { page_name: "Artists", route: "/artists", is_visible: true, display_order: 2 },
  { page_name: "Events",  route: "/events",  is_visible: true, display_order: 3 },
  { page_name: "About",   route: "/about",   is_visible: true, display_order: 4 },
  { page_name: "Contact", route: "/contact", is_visible: true, display_order: 5 },
];

// Module-level in-memory cache. Resets on full page reload, which is
// exactly what we want — fresh fetch on each hard navigation, cached
// for up to 60s during client-side nav between pages.
const CACHE_TTL_MS = 60_000;
let cache = null; // { fetchedAt: number, pages: [...], status: 'ready'|'error' }

async function fetchPageSettings() {
  const { data } = await api.get("/pages");
  if (!Array.isArray(data)) throw new Error("Unexpected response shape");
  return data;
}

export function PageSettingsProvider({ children }) {
  // If a fresh cache already exists (client-side nav), hydrate instantly.
  const now = Date.now();
  const hasFreshCache = cache && now - cache.fetchedAt < CACHE_TTL_MS;

  const [state, setState] = useState(() => {
    if (hasFreshCache) return { status: cache.status, pages: cache.pages };
    return { status: "loading", pages: FALLBACK_PAGES };
  });

  useEffect(() => {
    let cancelled = false;
    if (hasFreshCache) return;

    fetchPageSettings()
      .then((pages) => {
        if (cancelled) return;
        const sorted = [...pages].sort(
          (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
        );
        cache = { fetchedAt: Date.now(), pages: sorted, status: "ready" };
        setState({ status: "ready", pages: sorted });
      })
      .catch(() => {
        if (cancelled) return;
        // Spec: on error, fall back to showing all pages in default order.
        cache = { fetchedAt: Date.now(), pages: FALLBACK_PAGES, status: "error" };
        setState({ status: "error", pages: FALLBACK_PAGES });
      });

    return () => {
      cancelled = true;
    };
  }, [hasFreshCache]);

  return (
    <PageSettingsContext.Provider value={state}>
      {children}
    </PageSettingsContext.Provider>
  );
}

export function usePageSettings() {
  const ctx = useContext(PageSettingsContext);
  if (!ctx) {
    // Defensive default — should not happen since Provider wraps the app.
    return { status: "error", pages: FALLBACK_PAGES };
  }
  return ctx;
}

/**
 * Resolves visibility for a given pathname using the settings.
 * - Home ("/") is always visible as a safety measure.
 * - Exact route match governs first.
 * - Otherwise falls back to the longest route prefix (e.g. "/artists"
 *   protects "/artists/some-slug" and "/artwork/:id" via "/artists").
 * - Returns true when no matching entry exists (unknown routes are not
 *   blocked — let React Router's normal 404 handling take over).
 */
export function isRouteVisible(pathname, pages) {
  if (pathname === "/") return true;
  if (!Array.isArray(pages) || pages.length === 0) return true;

  // Exact match wins.
  const exact = pages.find((p) => p.route === pathname);
  if (exact) return exact.is_visible !== false;

  // Longest-prefix match for detail sub-routes.
  let best = null;
  for (const p of pages) {
    if (!p.route || p.route === "/") continue;
    if (pathname.startsWith(p.route + "/")) {
      if (!best || p.route.length > best.route.length) best = p;
    }
  }
  if (best) return best.is_visible !== false;

  return true;
}
